import { dateConverter, timeConverter } from '@/utils/helpers';
import {
  App,
  Button,
  Descriptions,
  Divider,
  Drawer,
  Select,
  Space,
} from 'antd';
import React, { FC, useEffect, useRef, useState } from 'react';
import { GalleryImages } from '../../ImagesLightBox';
import { SplashCarExterior } from '../../SplashCarExterior';
import { CarAreaCode, FORMAT_DATE_HH_MM } from '@/utils/constants';
import { FprInspection, LastScanModel } from '@/types';
import { FprDataGrid } from './FprDataGrid';
import { FprDetail } from './FprDetail';
import { useDamagesReroute } from '@/api/Damages';
import { FullscreenMap, Map, MapFullscreenData, MapHandle } from '../../Map';
import { faExpand, Icon } from '../../Icon';

interface FprDetailsProps {
  vehicleId?: number;
  lastScan?: LastScanModel;
  showReRoute: boolean;
  fprInspections: FprInspection[];
}
export const FprDetails: FC<FprDetailsProps> = ({
  vehicleId,
  lastScan,
  showReRoute,
  fprInspections,
}) => {
  const { message } = App.useApp();
  const mapRef = useRef<MapHandle>(null);
  const [data, setData] = useState<FprInspection>();
  const [area, setArea] = useState<CarAreaCode>();
  const [showFullMap, setShowFullMap] = useState(false);
  const [mapViewData, setMapViewData] = useState<MapFullscreenData>();
  const { mutateAsync: damagesRerouteAsync, isPending } = useDamagesReroute();

  const location =
    lastScan?.latitude && lastScan?.longitude
      ? {
          lat: lastScan.latitude,
          lng: lastScan.longitude,
          alt: lastScan.altitude,
          acc: lastScan.accuracy,
        }
      : undefined;
  const markers = location ? [location] : undefined;
  const circle = location
    ? { center: location, radius: location?.acc || 0 }
    : undefined;

  useEffect(() => {
    if (fprInspections.length > 0) setData(fprInspections[0]);
  }, [fprInspections]);

  const onDamageReroute = async () => {
    if (!vehicleId) return;
    const res = await damagesRerouteAsync({ vehicleId });
    if (!res.success) {
      message.error(res.message);
      return;
    }
  };

  return (
    <>
      <Space direction="vertical" className="w-100 mb-3">
        <div className="d-flex align-items-center justify-content-between">
          {fprInspections.length > 1 ? (
            <Select
              placeholder="Please choose a FPR inspection"
              style={{ width: 400 }}
              value={data?.id}
              options={fprInspections.map((o) => ({
                label: `Inspection ${o.id} (${timeConverter(o.inspectionDate)})`,
                value: o.id,
              }))}
              onChange={(id) =>
                setData(fprInspections.find((o) => o.id === id))
              }
            />
          ) : (
            <div></div>
          )}
          {showReRoute ? (
            <Button
              type="primary"
              disabled={isPending}
              onClick={onDamageReroute}
            >
              BMW Damage Re-Route
            </Button>
          ) : (
            <div></div>
          )}
        </div>
        {data ? (
          <>
            <Divider>Inspection detail</Divider>
            <Descriptions
              column={5}
              layout="vertical"
              items={[
                {
                  key: '5',
                  label: 'Inspection ID',
                  children: data.id,
                },
                {
                  key: '1',
                  label: 'Inspection date',
                  children: dateConverter(data.inspectionDate),
                },
                {
                  key: '2',
                  label: 'Inspection time',
                  children: timeConverter(
                    data.inspectionDate,
                    FORMAT_DATE_HH_MM,
                  ),
                },
                {
                  key: '3',
                  label: 'Inspector',
                  children: data.inspector,
                },
                {
                  key: '4',
                  label: 'Inspected At',
                  children: data.inspectionAt,
                },
              ]}
            />
            <Divider>Damages</Divider>
            <div className="d-flex">
              <SplashCarExterior
                width={300}
                area={area}
                onClick={(code) => setArea(code)}
              />
              <div
                className="d-flex flex-column gap-3"
                style={{ width: 'calc(100% - 300px)', paddingLeft: 20 }}
              >
                <FprDataGrid dataSource={data.damages} areaCode={area} />
                <Divider>Images</Divider>
                {data.images.length > 0 ? (
                  <div style={{ height: 200 }}>
                    <GalleryImages
                      actions={{ download: true }}
                      images={data.images.map((o) => ({
                        id: o.id,
                        imageFileUrl: o.imageFileUrl,
                        thumbnailImageUrl: o.thumbnailImageUrl,
                      }))}
                    />
                  </div>
                ) : (
                  <div className="d-flex align-items-center justify-content-center">
                    <img
                      className="mw-50"
                      src="images/car.png"
                      alt="no images"
                    />
                  </div>
                )}
              </div>
            </div>
            <Divider>Questions</Divider>
            <FprDetail detail={data.fprDetail} />
            <Divider>Last scan</Divider>
            <div className="d-flex flex-column">
              Last Scanned:
              {timeConverter(lastScan?.scanDate)}
              <span>
                {`Lat: ${location?.lat || 0} | Lng: ${
                  location?.lng || 0
                } | Acc: ${location?.acc || 0}m | Alt: ${location?.alt || 0}m`}
              </span>
              <div style={{ height: 300 }}>
                <div className="position-absolute w-100">
                  <div
                    className="position-relative d-flex justify-content-end"
                    style={{ zIndex: 2 }}
                  >
                    <div
                      className="map-full-screen"
                      style={{ marginRight: 25 }}
                    >
                      <Icon
                        icon={faExpand}
                        onClick={() => {
                          const view = mapRef.current?.getView();
                          if (view) {
                            setMapViewData(view);
                            setShowFullMap(true);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
                <Map
                  ref={mapRef}
                  markers={markers}
                  circle={circle}
                  center={location}
                  showSiteOverlays={true}
                />
              </div>
            </div>
          </>
        ) : null}
      </Space>
      <Drawer
        title="Last scan FPOR inspection"
        placement="right"
        onClose={() => setShowFullMap(false)}
        open={showFullMap}
        classNames={{ wrapper: 'vw-100', header: 'p-2', body: 'p-2' }}
      >
        <FullscreenMap
          markers={markers}
          circle={circle}
          zoom={mapViewData?.zoom}
          bounds={mapViewData?.bounds}
        />
      </Drawer>
    </>
  );
};
